import { useState } from "react";

import { AlertTriangle, Download, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import type { DraftConflict } from "../editor/draft-version";

/**
 * Shown when the server refuses a save because another tab or user saved first
 * (MDRS-554 / audit RB-8).
 *
 * Two things make this more than a notification:
 *
 * 1. **Download my copy comes first.** Reloading is unrecoverable — it throws
 *    away everything this tab has typed since the other save landed. Telling a
 *    doctor that without handing them their own work would be a worse
 *    experience than the silent overwrite this ticket fixes: at least that one
 *    didn't make them watch.
 * 2. **It can't be dismissed.** Autosave is stopped for as long as the conflict
 *    stands, so an Escape-dismissable dialog would drop the user back into an
 *    editor that silently saves nothing — the original bug, now with a warning
 *    they've already closed.
 */
type Props = {
  /** Null = no conflict; the dialog renders nothing. */
  conflict: DraftConflict | null;
  /** Resolves false when no copy could be produced (editor not mounted, or the
   *  serializer threw) — the user must be told rather than left guessing. */
  onDownloadCopy: () => Promise<boolean>;
  onReload: () => void;
};

const formatSavedAt = (iso: string | null): string | null => {
  if (!iso) return null;
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return null;
  return when.toLocaleString();
};

/**
 * Split in two on purpose. `ReportSectionEditor` mounts this permanently, so
 * `return null` only HIDES the dialog — it keeps the component's state. A user
 * who downloaded a copy during one conflict would then open the next one
 * already being told "Saved to your downloads as a .docx", and could reload on
 * the strength of a message about a file that doesn't contain these edits.
 * Keying the inner dialog on the conflict's identity gives each conflict its
 * own state, which is the only way this dialog can keep its promise that
 * nobody discards work they haven't been handed a copy of.
 */
export const DraftConflictModal = ({ conflict, ...handlers }: Props) =>
  conflict ? (
    <DraftConflictDialog
      key={`${conflict.currentVersion}:${conflict.savedAt ?? ""}`}
      conflict={conflict}
      {...handlers}
    />
  ) : null;

const DraftConflictDialog = ({
  conflict,
  onDownloadCopy,
  onReload,
}: Omit<Props, "conflict"> & { conflict: DraftConflict }) => {
  const [downloading, setDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  const savedAt = formatSavedAt(conflict.savedAt);

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadFailed(false);
    try {
      const ok = await onDownloadCopy();
      setDownloaded(ok);
      setDownloadFailed(!ok);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open>
      <DialogContent
        className="rounded-2xl bg-background px-8 py-10"
        showCloseButton={false}
        // No exit that isn't a decision — see the note at the top of the file.
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader className="flex flex-col items-center gap-6 text-center">
          <figure className="flex size-14 items-center justify-center rounded-full bg-amber-100">
            <AlertTriangle className="size-7 text-amber-600" aria-hidden />
          </figure>
          <DialogTitle>This report was changed somewhere else</DialogTitle>
          <DialogDescription className="text-left">
            {savedAt
              ? `Another tab — or another person working on this case — saved this report at ${savedAt}. `
              : "Another tab — or another person working on this case — saved this report after you opened it. "}
            Your changes in this tab haven&apos;t been saved, and saving now would
            overwrite theirs.
            <br />
            <br />
            Download a copy of your version first, then reload to continue from the
            latest one.
          </DialogDescription>
        </DialogHeader>

        {downloadFailed && (
          <p className="text-center text-sm text-destructive">
            Your copy couldn&apos;t be prepared. Copy any text you need before
            reloading.
          </p>
        )}
        {downloaded && !downloadFailed && (
          <p className="text-muted-foreground text-center text-sm">
            Saved to your downloads as a .docx.
          </p>
        )}

        <div className="mt-2 flex flex-col gap-3">
          <Button
            variant="simple-outline"
            onClick={handleDownload}
            disabled={downloading}
          >
            <Download className="mr-2 size-4" aria-hidden />
            {downloading ? "Preparing…" : "Download my copy"}
          </Button>
          <Button onClick={onReload}>
            <RefreshCw className="mr-2 size-4" aria-hidden />
            Reload the latest version
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
